import React, { useEffect, useMemo, useRef, useState } from "react";
import { App as AntdApp, Button, Card, ConfigProvider, Dropdown, Layout, Menu, Progress, Result, Space, Spin, Tag, Tooltip, Typography, Upload } from "antd";
import { AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined, ClusterOutlined, DeploymentUnitOutlined, FileAddOutlined, KeyOutlined, LoadingOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, PictureOutlined, PlusCircleOutlined, SettingOutlined, TagOutlined, ThunderboltOutlined, UserOutlined } from "@ant-design/icons";

import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useSSEStatus } from "@modules/realtime/hooks/useSSEStatus.js"
import api from "@/api/axios.js";

// Carico le risorse logo esterne
import logo from "@/assets/logo.webp";
import brand from "@/assets/brand.webp";

import "antd/dist/reset.css";

// Importo i componenti
import OrgTreePage from "@modules/org/pages/OrgTreePage.jsx";
import VolantiniPage from "@modules/flyers/pages/VolantiniPage.jsx";
import PromosPage from "@modules/promos/pages/PromosPage.jsx";
import PromoDetailPage from "@modules/promos/pages/PromoDetailPage.jsx";
import BackgroundOrbs from "@shared/components/effects/BackgroundOrbs.jsx";
import NewVersionNotice from "@shared/components/NewVersionNotice.jsx";
import useUppercaseSelectionShortcut from "@shared/hooks/useUppercaseSelectionShortcut.js";

import locale from "antd/locale/it_IT";
import dayjs from 'dayjs';
import 'dayjs/locale/it'
import FlyerManagePage from "@modules/flyers/pages/FlyerManagePage.jsx";
import FlyerPreprocessPage from "@modules/flyers/pages/FlyerPreprocessPage.jsx";
import VariantPreprocessPage from "@modules/flyers/pages/VariantPreprocessPage.jsx";
import WebPToolPage from "@modules/webp/pages/WebPToolPage.jsx";
import SettingsPage from "@modules/settings/pages/SettingsPage.jsx";

// Auth / RBAC
import { useAuth } from "@modules/auth/AuthContext.jsx";
import RequireMenu from "@modules/auth/RequireMenu.jsx";
import LoginPage from "@modules/auth/pages/LoginPage.jsx";
import ChangePasswordModal from "@modules/auth/components/ChangePasswordModal.jsx";
import UsersPage from "@modules/users/pages/UsersPage.jsx";
import { MENU_PRESENTATION, ROUTE_MAP, pathKeyForPath } from "@modules/auth/menuCatalog.js";

dayjs.locale('it')

const BRAND = {
	primary: "#E31E24",
	dark: "#2D2D2D",
	light: "#f7f7f7"
};

// Tema AntD (statico) condiviso fra gate e shell.
const THEME_CFG = {
	token: {
		colorPrimary: BRAND.primary,
		colorInfo: BRAND.primary,
		colorText: BRAND.dark,
		fontSize: 14,
		borderRadius: 8
	}
};

const { Header, Sider, Content, Footer } = Layout;
const { Title, Text } = Typography;

/** STYLE layouts **/
const headerStyle = {
	position: "sticky",
	top: 0,
	zIndex: 11,
	background: "rgba(255,255,255,0.75)",
	paddingLeft: "20px",
	backdropFilter: "blur(8px)",
	borderBottom: "1px solid #eee",
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	boxShadow: "0 0 10px #f8595a88",
};
const siderStyle = {
	position: "sticky",
	height: "100vh",
	overflowY: "hidden",
	top: 0,
	zIndex: 12,
	background: "rgba(255,255,255,0.75)",
	backdropFilter: "blur(8px)",
	borderRight: "1px solid #eee",
	boxShadow: "0 0 10px #f8595a88",
};
const menuStyle = {
	height: "100vh",
	overflowY: "auto",
	borderRight: 0,
	background: "transparent",
	padding: 8
};

export default function App() {
	const { status, serverUnavailable } = useAuth();
	useUppercaseSelectionShortcut();

	if (status === "authed")
		return <AuthedShell />;

	return (
		<ConfigProvider locale={locale} theme={THEME_CFG}>
			<AntdApp>
				{status === "loading" ? (
					<div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center", background: BRAND.light }}>
						<Spin size="large" />
						{serverUnavailable && <span style={{ color: BRAND.dark }}>Connessione al server in corso…</span>}
					</div>
				) : (
					<Routes>
						<Route path="/login" element={<LoginPage />} />
						<Route path="*" element={<Navigate to="/login" replace />} />
					</Routes>
				)}
			</AntdApp>
		</ConfigProvider>
	);
}

function AuthedShell() {
	const { user, logout, hasMenu, allowedMenuItems, landingMenuItem } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const isPreprocess = useMemo(() => location.pathname.startsWith("/flyerpreprocess") || location.pathname.startsWith("/variant-preprocess"), [location.pathname]);
	const wasPreprocessRef = useRef(false);

	const pathKey = useMemo(() => pathKeyForPath(location.pathname), [location.pathname]);

	const [openWizardFlag, setOpenWizardFlag] = useState(false);
	const [pwdOpen, setPwdOpen] = useState(false);
	const [pdfHealth, setPdfHealth] = useState({ status: "loading", label: "PDF: verifica..." });

	const { status } = useSSEStatus("sys");

	const [collapsed, setCollapsed] = useState(() => {
		try {
			return localStorage.getItem("dash.collapsed") === "true"
		}
		catch (e) {
			return false;
		}
	});

	const menuItems = useMemo(() => (
		MENU_PRESENTATION
			.filter(m => allowedMenuItems.includes(m.key))
			.map(({ key, Icon, label }) => ({ key, icon: <Icon />, label }))
	), [allowedMenuItems]);

	useEffect(() => {
		try {
			localStorage.setItem("dash.collapsed", collapsed);
		}
		catch (e) {
			console.error("dash -> localStorage error", e);
		}
	}, [collapsed]);

	useEffect(() => {
		document.documentElement.classList.add("allow-motion");
	}, []);

	useEffect(() => {
		if (isPreprocess && !wasPreprocessRef.current && !collapsed) {
			setCollapsed(true);
		}
		wasPreprocessRef.current = isPreprocess;
	}, [isPreprocess, collapsed]);

	useEffect(() => {
		let active = true;

		const fetchHealth = async () => {
			try {
				const res = await api.get("/api/utils/health");
				const renderer = res.data?.pdfRenderer;
				if (!renderer) {
					throw new Error("Missing pdfRenderer");
				}

				let label = "PDF: ok";
				let status = renderer.ok ? "ok" : "missing";

				if (renderer.platform === "linux") {
					if (renderer.ok) {
						label = `PDF: ${renderer.preferred || "poppler"} ok`;
					} else {
						label = "PDF: poppler assente";
					}
				} else if (renderer.platform === "darwin") {
					label = renderer.ok
						? `PDF: ${renderer.preferred || "quicklook"} ok`
						: "PDF: renderer assente";
				} else {
					label = "PDF: fallback";
					status = "ok";
				}

				if (active) {
					setPdfHealth({ status, label });
				}
			} catch (err) {
				if (active) {
					setPdfHealth({ status: "error", label: "PDF: errore" });
				}
			}
		};

		fetchHealth();

		return () => {
			active = false;
		};
	}, []);

	const handleMenuClick = ({ key }) => {
		navigate(ROUTE_MAP[key] ?? "/");
	};

	const sseColor = status === "open" ? BRAND.primary : BRAND.dark;
	const sseIcon = status === "open" ? <ThunderboltOutlined /> : (status === "connecting" ? <LoadingOutlined /> : <CloseCircleOutlined />);
	const sseLabel = status === "open" ? "SSE: pronto" : (status === "connecting" ? "SSE: connessione…" : "SSE: non connesso");
	const pdfColor = pdfHealth.status === "ok" ? "green" : (pdfHealth.status === "loading" ? "default" : "red");
	const pdfIcon = pdfHealth.status === "ok" ? <CheckCircleOutlined /> : (pdfHealth.status === "loading" ? <LoadingOutlined /> : <CloseCircleOutlined />);
	const pdfLabel = pdfHealth.label;

	const userMenu = {
		items: [
			{ key: "whoami", disabled: true, label: `${user?.displayName || user?.username} · ${user?.role}` },
			{ type: "divider" },
			{ key: "pwd", icon: <KeyOutlined />, label: "Cambia password", onClick: () => setPwdOpen(true) },
			{ key: "logout", icon: <LogoutOutlined />, danger: true, label: "Esci", onClick: () => logout() }
		]
	};

	// Mai "/login" come ripiego: qui dentro non esiste, finiva su "/" e tornava indietro
	// all'infinito bloccando la scheda. Senza pagina iniziale si mostra un avviso.
	const landingRoute = ROUTE_MAP[landingMenuItem] || null;

	return (
		<ConfigProvider locale={locale} theme={THEME_CFG}>
			<AntdApp>
				<NewVersionNotice />
				<Layout style={{ minHeight: "100vh", background: "transparent" }}>
					<BackgroundOrbs BRAND={BRAND} />

					<Sider
						trigger={null}
						collapsible
						collapsed={collapsed}
						width={260}
						style={siderStyle}
					>
						<div style={{ padding: 16, display: "flex", ...(collapsed ? { justifyContent: "center" } : { alignItems: "center" }), gap: 10 }}>
							<img src={collapsed ? brand : logo} alt="VolantinoPiù" style={{ height: 36, objectFit: "contain" }} />
						</div>

						<Menu
							mode="inline"
							selectedKeys={[pathKey]}
							onClick={handleMenuClick}
							items={menuItems}
							style={menuStyle}
						/>
					</Sider>

					<Layout>
						<Header style={headerStyle}>
							<Space align="center" size="middle">
								<Button
									type="text"
									icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
									onClick={() => setCollapsed(!collapsed)}
								/>
								<Title level={4} style={{ margin: 0 }}>Console Operativa</Title>
								<Tag color="green">alpha</Tag>
							</Space>

							<Space>
								<Tooltip title={sseLabel}>
									<Tag icon={sseIcon} color={sseColor}>
										{sseLabel}
									</Tag>
								</Tooltip>
								<Tooltip title={pdfLabel}>
									<Tag icon={pdfIcon} color={pdfColor}>
										{pdfLabel}
									</Tag>
								</Tooltip>
								<Dropdown menu={userMenu} trigger={["click"]}>
									<Button type="text" icon={<UserOutlined />}>
										{user?.displayName || user?.username}
									</Button>
								</Dropdown>
							</Space>
						</Header>


						<Content style={{ padding: 24, position: "relative" }}>
							<Routes>
								<Route
									path="/"
									element={hasMenu("dashboard")
										? <Dashboard onOpenWizard={() => {
											setOpenWizardFlag(true);
											navigate("/flyers");
										}} />
										: (landingRoute
											? <Navigate to={landingRoute} replace />
											: <Result
												status="403"
												title="Nessuna sezione disponibile"
												subTitle="Il tuo utente non ha sezioni abilitate: chiedi a un amministratore di assegnartene almeno una."
												extra={<Button onClick={() => logout()}>Esci</Button>}
											/>)}
								/>
								<Route
									path="/flyers"
									element={<RequireMenu menuKey="flyers"><VolantiniPage
										openOnMount={openWizardFlag}
										onOpened={() => setOpenWizardFlag(false)}
										onManage={(flyer, pageKey) => {
											const id = flyer._id || flyer.id;
											const base = `/flyermanage/${id}`;
											const next = pageKey ? `${base}/${pageKey}` : base;
											navigate(next, { state: { flyerName: flyer.name || "Volantino" } });
										}}
									/></RequireMenu>}
								/>
								<Route path="/flyers/trash" element={<RequireMenu menuKey="flyers"><VolantiniPage mode="trash" /></RequireMenu>} />
								<Route path="/flyermanage/:flyerId/:page?" element={<FlyerManagePage />} />
								<Route path="/flyerpreprocess/:flyerId" element={<FlyerPreprocessPage />} />
								<Route path="/variant-preprocess/:variantId" element={<VariantPreprocessPage />} />
								<Route path="/promos" element={<RequireMenu menuKey="promos"><PromosPage /></RequireMenu>} />
								<Route path="/promos/:promoId" element={<RequireMenu menuKey="promos"><PromoDetailPage /></RequireMenu>} />
								<Route path="/org" element={<RequireMenu menuKey="org"><OrgTreePage /></RequireMenu>} />
								<Route path="/cards" element={<RequireMenu menuKey="cards"><CardsGrid /></RequireMenu>} />
								<Route path="/webp" element={<RequireMenu menuKey="webp"><WebPToolPage /></RequireMenu>} />
								<Route path="/logs" element={<RequireMenu menuKey="logs"><RealtimePanel /></RequireMenu>} />
								<Route path="/settings" element={<RequireMenu menuKey="settings"><SettingsPage /></RequireMenu>} />
								<Route path="/users" element={<RequireMenu menuKey="users"><UsersPage /></RequireMenu>} />
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						</Content>

						{!isPreprocess && (
							<Footer style={{ background: "transparent", textAlign: "center", color: "#666" }}>
								VolantinoPiù ❤️ SoftInnova Solutions - v 1.0.0
							</Footer>
						)}
					</Layout>
				</Layout>

				<StyleBlock />
				<ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
			</AntdApp>
		</ConfigProvider>
	);
}

function Dashboard({ onOpenWizard }) {
	return (
		<div className="wow-hero">
			<div className="wow-outer">
				<div className="wow-ambient" />
				<button
					className="wow-inner"
					type="button"
					onClick={onOpenWizard}
					aria-label="Crea nuovo volantino"
				>
					<img
						className="wow-logo"
						src={brand}
						alt="VolantinoPiu"
						draggable="false"
					/>
				</button>

				<Typography.Title level={4} className="wow-logo-title" aria-hidden="true">
					Volantino <PlusCircleOutlined />
				</Typography.Title>
			</div>
		</div>
	);
}

function CardsGrid() {
	return (
		<Card className="card-elev">
			<Title level={5}>Cards generate</Title>
			<Text>Grid anteprime + stato match. (placeholder)</Text>
		</Card>
	);
}

function WebPTool({ onUpload, draggerProps, uploading }) {
	return (
		<Card className="card-elev">
			<Title level={5}>Converti immagini in WebP</Title>
			<Text>Carica una cartella di immagini. Ridimensionamento + conversione lato server.</Text>
			<Upload.Dragger {...draggerProps} onChange={onUpload} disabled={uploading} style={{ marginTop: 12 }} />
		</Card>
	);
}

function RealtimePanel() {
	return (
		<Card className="card-elev">
			<Title level={5}>Realtime & Log</Title>
			<Text>Colleghiamo qui gli eventi SSE (ingest, crop, ocr…).</Text>
			<div style={{ marginTop: 12 }}>
				<Progress percent={72} status="active" />
			</div>
		</Card>
	);
}

function StyleBlock() {
	return (
		<style>
			{`
				.grid {
				  display: grid;
				  gap: 20px;
				}

				.grid-2 {
				  grid-template-columns: repeat(2, minmax(0, 1fr));
				}

				.grid-3 {
				  grid-template-columns: repeat(3, minmax(0, 1fr));
				}

				@media (max-width: 1100px) {
				  .grid-3 {
					grid-template-columns: 1fr;
				  }

				  .grid-2 {
					grid-template-columns: 1fr;
				  }
				}

				.card-elev {
				  backdrop-filter: blur(8px);
				  background: rgba(255, 255, 255, 0.82);
				  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
				  border: 1px solid rgba(0, 0, 0, 0.04);
				}

				.card-elev.accent {
				  background: linear-gradient(135deg, ${BRAND.primary}, #ff6b6b);
				  box-shadow: 0 14px 40px rgba(227, 30, 36, 0.35);
				}

				.list {
				  margin: 0;
				  padding-left: 18px;
				}

				.checklist {
				  display: flex;
				  flex-wrap: wrap;
				  gap: 8px;
				  margin-top: 8px;
				}

				/** Pulsantone WOW **/

				:root {
				  --wow-accent: ${BRAND.primary};
				}

				.wow-hero {
				  min-height: calc(100vh - 180px);
				  display: flex;
				  flex-direction: column;
				  align-items: center;
				  justify-content: center;
				  gap: 16px;
				  text-align: center;
				  position: relative;
				}

				.wow-outer {
				  width: 420px;
				  height: 420px;
				  border-radius: 50%;
				  background:
					radial-gradient(closest-side,
					  color-mix(in srgb, var(--wow-accent) 30%, transparent) 0%,
					  color-mix(in srgb, var(--wow-accent) 16%, transparent) 55%,
					  transparent 78%);
				  position: relative;
				  isolation: isolate;
				  display: flex;
				  align-items: center;
				  justify-content: center;
				  box-shadow:
					0 30px 78px color-mix(in srgb, var(--wow-accent) 40%, rgba(0,0,0,.25)),
					inset 0 12px 20px rgba(255,255,255,.18),
					inset 0 -18px 28px rgba(0,0,0,.22);
				}

				@keyframes wow-ripple-strong {
				  0%   { transform: translate(-50%, -50%) scale(.75); opacity: .72; }
				  70%  { transform: translate(-50%, -50%) scale(2.35); opacity: 0; }
				  100% { transform: translate(-50%, -50%) scale(2.35); opacity: 0; }
				}

				.allow-motion .wow-outer::before,
				.allow-motion .wow-outer::after {
				  content: "";
				  position: absolute;
				  top: 50%; left: 50%;
				  width: 44%; height: 44%;
				  border-radius: 50%;
				  transform: translate(-50%, -50%) scale(.75);
				  transform-origin: 50% 50%;
				  will-change: transform, opacity;
				  background: radial-gradient(circle,
							  rgba(255,255,255,0) 52%,
							  rgba(255,255,255,.9) 58%,
							  rgba(255,255,255,0) 64%);
				  box-shadow:
					0 0 0 6px color-mix(in srgb, var(--wow-accent) 24%, transparent),
					0 0 30px color-mix(in srgb, var(--wow-accent) 22%, transparent);
				  filter: blur(1.6px);
				  animation: wow-ripple-strong 5s infinite ease-out;
				  animation-fill-mode: both;
				  z-index: 0;
				  pointer-events: none;
				}

				.allow-motion .wow-outer::after {
				  animation-delay: 3.5s;
				}

				.wow-inner {
				  width: 260px; height: 260px; border-radius: 50%;
				  background: rgba(255,255,255,.10);
				  -webkit-backdrop-filter: blur(16px) saturate(120%) brightness(110%);
						  backdrop-filter: blur(16px) saturate(120%) brightness(110%);
				  position: relative; z-index: 2;
				  display: flex; align-items: center; justify-content: center;
				  cursor: pointer; outline: none;
				  transition: transform .22s ease, box-shadow .22s ease, filter .22s ease;
				  box-shadow:
					0 18px 48px rgba(0,0,0,.28),
					inset 0 1px 0 rgba(255,255,255,.35),
					inset 0 -12px 22px rgba(0,0,0,.20);
				}

				.wow-inner::before {
				  content: "";
				  position: absolute;
				  inset: -12px;
				  border-radius: 50%;
				  background:
					radial-gradient(closest-side,
					  rgba(255,255,255,.92) 96%,
					  rgba(255,255,255,0) 100%);
				  filter: blur(3px);
				  z-index: 0;
				  pointer-events: none;
				}

				.wow-inner:hover,
				.wow-inner:focus-visible {
				  transform: translateY(-2px) scale(1.02);
				  box-shadow:
					0 24px 58px rgba(0,0,0,.32),
					inset 0 2px 0 rgba(255,255,255,.40),
					inset 0 -14px 24px rgba(0,0,0,.22);
				  filter: saturate(1.06);
				}

				.wow-icon {
				  font-size: 72px;
				  color: #fff;
				  text-shadow:
					0 2px 6px rgba(0,0,0,.25),
					0 10px 24px rgba(0,0,0,.18);
				}

				.wow-title { margin: 10px 0 0; }
				.wow-subtitle { color: rgba(0,0,0,.55); }

				@supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
				  .wow-inner {
					background: rgba(255,255,255,.18);
				  }
				}

				.wow-logo {
				  width: 46%;
				  height: 46%;
				  object-fit: contain;
				  image-rendering: -webkit-optimize-contrast;
				  pointer-events: none;
				  filter:
					drop-shadow(0 2px 6px rgba(0,0,0,.25))
					drop-shadow(0 10px 20px rgba(0,0,0,.18));
				}

				@media (max-width: 520px) {
				  .wow-outer { width: 320px; height: 320px; }
				  .wow-inner { width: 200px; height: 200px; }
				  .wow-icon  { font-size: 56px; }
				}

				.wow-outer .wow-ambient {
				  position: absolute;
				  inset: -20px;
				  border-radius: 50%;
				  background: radial-gradient(closest-side,
							  color-mix(in srgb, var(--wow-accent) 32%, transparent) 0%,
							  color-mix(in srgb, var(--wow-accent) 14%, transparent) 42%,
							  transparent 70%);
				  filter: blur(18px);
				  z-index: 0;
				  pointer-events: none;
				}

				.wow-logo-title {
				  position: absolute;
				  left: 50%;
				  top: 66%;
				  transform: translateX(-50%);
				  width: 72%;
				  margin: 0;
				  text-align: center;
				  line-height: 1.05;
				  font-weight: 800;
				  font-size: clamp(14px, 2.2vw, 18px);
				  color: rgba(0,0,0,.82);
				  text-shadow: 0 1px 2px rgba(255,255,255,.6);
				  pointer-events: none;
				  z-index: 5;
				}

				@media (max-width: 520px) {
				  .wow-logo-title {
					top: 64%; /* micro-tweak su mobile */
				  }
				}

			`}
		</style>
	);
}
